/**
 * OS-level notifications, for when the portal is not the tab being looked at.
 *
 * The in-app toast is the primary signal and is enough whenever the portal is on
 * screen. This exists for the case the toast cannot cover: an admin working in
 * another tab or another window entirely, which is most of the time.
 *
 * Two rules keep it from becoming the kind of feature people turn off:
 *
 *   1. **Permission is only ever requested from a user gesture.** Asking on mount
 *      is the pattern browsers added permission-request throttling to punish, and
 *      Chrome now shows an unprompted request as a muted icon nobody sees. The
 *      ask is attached to the alerts toggle instead, so it happens when somebody
 *      has just said they want alerts.
 *   2. **Nothing is shown while the tab is visible.** A notification tray entry
 *      for something already on screen is noise.
 */

/** Whether this browser can do it at all. */
export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export type NotificationPermissionState = 'unsupported' | NotificationPermission;

export function notificationPermission(): NotificationPermissionState {
  if (!notificationsSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Ask, once, from a click.
 *
 * Resolves to the resulting state either way — a denial is an answer, and the
 * caller uses it to stop offering. Never throws: Safari resolves the legacy
 * callback form and older browsers reject outright.
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!notificationsSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Raise one, if it would actually tell somebody something.
 *
 * `tag` collapses repeats: a second alert for the same conversation replaces the
 * first in the tray rather than stacking under it.
 */
export function showDesktopNotification(input: {
  title: string;
  body: string;
  tag: string;
  onClick?: () => void;
}): void {
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  // The toast has this covered when the portal is the tab in front of them.
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;

  try {
    const notification = new Notification(input.title, {
      body: input.body,
      tag: input.tag,
      // Ours is a two-note chime through Web Audio; the OS sound on top of it is
      // one alert too many.
      silent: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      input.onClick?.();
    };
  } catch {
    // Some browsers throw for `new Notification` on mobile, where it is only
    // available through a service worker. The toast and the chime remain.
  }
}
